import {
  CategoryEntity,
  EntityRefUtil,
  formatDateWithUserTimezone,
  FormElementKind,
  GetIssuesRequestDTO,
  GetIssuesResponseDTO,
  GetUnitsResponseDTO,
  IssueDTO,
  IssueSeverity,
  IssueStatus,
  TagEntity,
} from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { AccessorKeyColumnDef, createColumnHelper } from '@tanstack/react-table';
import { capitalize } from 'lodash';

import WarningIcon from '@/components/icons/warning';
import { ASSET_SECTION_LABEL } from '@/components/table-page/columns/asset-tags-column';
import { createTagsColumn } from '@/components/table-page/columns/tags-column';
import { Badge, badgeVariants } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { UserAvatarLabel } from '@/components/users/user-avatar-label';
import { cn, initials } from '@/lib/utils';
import { ApiService } from '@/services/api-v2';
import { useUserStore } from '@/stores/user.store';
import { getAvatarBackgroundColorClass } from '@/utils/avatar';
import { getIssueSeverityColorClass, getIssueStatusColorClass, ISSUE_SEVERITY_OPTIONS, ISSUE_STATUS_OPTIONS } from '@/utils/issue';

const columnHelper = createColumnHelper<GetIssuesResponseDTO['data'][number]>();

export const createAssetIssueTableColumns = (opts?: {
  onWorkOrderClick?: (workOrderId: string) => void;
  onRepairClick?: (repairId: string) => void;
  units?: GetUnitsResponseDTO['data'];
}): AccessorKeyColumnDef<IssueDTO>[] => {
  const userTimezone = useUserStore.getState().user?.timezone;
  return [
    columnHelper.display({
      id: 'select',
      header: ({ table }) => (
        <div className="flex w-full justify-center">
          <label className="-m-2 p-2">
            <Checkbox
              checked={table.getIsAllPageRowsSelected()}
              onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
              aria-label="Select all"
            />
          </label>
        </div>
      ),
      cell: ({ row }) => (
        <div className="flex w-full justify-center">
          <label className="-m-2 p-2" onClick={(e) => e.stopPropagation()}>
            <Checkbox checked={row.getIsSelected()} onCheckedChange={(value) => row.toggleSelected(!!value)} aria-label="Select row" />
          </label>
        </div>
      ),
      meta: {
        cellClassName: 'w-[40px] min-w-[40px] max-w-[40px]',
      },
      enableSorting: false,
      enableHiding: false,
    }),
    columnHelper.accessor('severity', {
      header: 'Severity',
      cell: ({ getValue }) => {
        const value = getValue();
        const issueSeverityColorClass = getIssueSeverityColorClass(value as IssueSeverity);
        const issueSeverityIcon =
          value === IssueSeverity.Critical ? (
            <WarningIcon size={20} className="text-default-red" />
          ) : (
            <div className={cn('mx-[3px] h-[15px] w-[15px] rounded-full border-[3px]', issueSeverityColorClass)} />
          );
        return (
          <div className="flex items-center gap-[5px]">
            {issueSeverityIcon}
            <span className="text-sm">{value ? capitalize(value) : '-'}</span>
          </div>
        );
      },
      getGroupingValue: (row) => (row.severity ? capitalize(row.severity) : 'None'),
      enableGrouping: true,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'w-[120px]',
        filterType: 'multi-select',
        getFilterOptionsFn: async () => {
          return ISSUE_SEVERITY_OPTIONS;
        },
      },
    }),
    columnHelper.accessor('number', {
      id: 'number',
      header: 'Issue ID',
      cell: ({ getValue }) => {
        const value = getValue();
        return value ? <span>{EntityRefUtil.createIssueRef(value)}</span> : <span className="text-secondary-text">-</span>;
      },
      meta: {
        filterType: 'text',
      },
      enableColumnFilter: true,
    }),
    columnHelper.display({
      id: 'asset',
      header: 'Asset',
      cell: ({ row }) => {
        const value = row.original.asset;
        return value ? <span>{EntityRefUtil.createAssetLabel(value)}</span> : <span className="text-secondary-text">-</span>;
      },
      meta: { columnGroup: ASSET_SECTION_LABEL },
    }),
    columnHelper.accessor('workOrder', {
      id: 'workOrder',
      header: 'Work Order',
      cell: ({ getValue }) => {
        const value = getValue();
        if (!value) {
          return <span className="text-secondary-text">-</span>;
        }

        return (
          <Button
            variant="link"
            className="h-auto p-0 text-left"
            onClick={(e) => {
              e.stopPropagation();
              opts?.onWorkOrderClick?.(value.id);
            }}>
            {EntityRefUtil.createWorkOrderRef(value.number)}
          </Button>
        );
      },
      getGroupingValue: (row) => row.workOrder?.number || 'None',
      meta: {
        filterType: 'text',
      },
      enableColumnFilter: true,
      enableGrouping: true,
    }),
    columnHelper.accessor((row) => formatDateWithUserTimezone(new Date(row.createdAt), userTimezone), {
      id: 'createdDate',
      header: 'Created Date',
      enableSorting: true,
      enableGrouping: true,
      meta: {
        filterType: 'date-range',
      },
    }),
    columnHelper.accessor('location', {
      header: 'Location',
      cell: ({ getValue }) => {
        const value = getValue();
        return value ? <span>{value.name}</span> : <span className="text-secondary-text">-</span>;
      },
      getGroupingValue: (row) => row.location?.name || 'None',
      enableGrouping: true,
      enableColumnFilter: true,
      meta: {
        filterType: 'async-multi-select',
        getFilterOptionsFn: async (inputValue, value) => {
          const filters = inputValue ? { filters: { search: inputValue } } : undefined;
          const { data } = await (await ApiService.GetLocations({ limit: 100, ...filters })).json();

          const resultsMap = new Map(data.map((location) => [location.id, { label: location.name, value: location.id }]));

          if (value?.length) {
            const missingIds = Array.isArray(value) ? value.filter((id) => !resultsMap.has(id)) : [];
            if (missingIds.length) {
              const { data: missingLocations } = await (
                await ApiService.GetLocations({
                  limit: 100,
                  filters: { fields: { id: missingIds } },
                })
              ).json();

              for (const location of missingLocations) {
                resultsMap.set(location.id, { label: location.name, value: location.id });
              }
            }
          }

          return Array.from(resultsMap.values());
        },
      },
    }),
    columnHelper.accessor('status', {
      header: 'Status',
      cell: ({ getValue }) => {
        const value = getValue();
        const issueStatusColorClass = getIssueStatusColorClass(value as IssueStatus);

        return value != null ? (
          <span className={cn(badgeVariants({ size: 'sm' }), issueStatusColorClass)}>{capitalize(value)}</span>
        ) : (
          <span className="text-secondary-text">-</span>
        );
      },
      getGroupingValue: (row) => (row.status ? capitalize(row.status) : 'None'),
      enableGrouping: true,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'p-[5px]',
        filterType: 'multi-select',
        disableNullFilter: true,
        getFilterOptionsFn: async () => {
          return ISSUE_STATUS_OPTIONS;
        },
      },
    }),
    columnHelper.display({
      id: 'fieldReference',
      header: 'Field Reference',
      cell: ({ row }) => {
        const fieldReference = row.original.fieldReference;
        return fieldReference ? <span>{fieldReference}</span> : <span className="text-secondary-text">-</span>;
      },
    }),
    columnHelper.accessor('title', {
      header: 'Title',
      cell: ({ getValue }) => {
        const title = getValue();
        return title ? <span className="block max-w-[500px] truncate">{title}</span> : <span className="text-secondary-text">-</span>;
      },
      enableColumnFilter: false,
      enableGrouping: false,
      enableSorting: false,
    }),
    columnHelper.accessor('description', {
      header: 'Description',
      cell: ({ getValue }) => {
        const description = getValue();
        return description ? <span className="block max-w-[500px] truncate">{description}</span> : <span className="text-secondary-text">-</span>;
      },
    }),
    columnHelper.accessor('repair', {
      id: 'repair',
      header: 'Repair Record',
      cell: ({ getValue }) => {
        const value = getValue();

        if (!value?.id) {
          return <span className="text-secondary-text">-</span>;
        }

        return (
          <Button
            variant="link"
            className="h-auto p-0 text-left"
            onClick={(e) => {
              e.stopPropagation();
              opts?.onRepairClick?.(value.id);
            }}>
            {EntityRefUtil.createRepairRef(value.number)}
          </Button>
        );
      },
      meta: {
        filterType: 'text',
      },
      enableColumnFilter: true,
      enableGrouping: true,
      getGroupingValue: (row) => row.repair?.number || 'None',
    }),
    columnHelper.accessor((row) => row.user, {
      id: 'createdBy',
      header: 'Created By',
      cell: ({ row }) => {
        const issue = row.original;
        const createdBy = issue.user;
        const fullName = EntityRefUtil.createUserFullNameRef(createdBy);
        const value = fullName || '-';
        const avatarBackgroundColorClass = getAvatarBackgroundColorClass(createdBy?.firstName || '');

        const createdByFullInitials = initials(fullName);
        return (
          <UserAvatarLabel
            value={value}
            operatedByFullInitials={createdByFullInitials}
            avatarUrl={createdBy?.avatarUrl || ''}
            avatarBackgroundColorClass={avatarBackgroundColorClass}
          />
        );
      },
      getGroupingValue: (row) => {
        return EntityRefUtil.createUserFullNameRef(row.user, 'None');
      },
      enableGrouping: true,
      enableColumnFilter: true,
      meta: {
        filterType: 'async-multi-select',
        getFilterOptionsFn: async (inputValue, value) => {
          const filters = inputValue ? { filters: { search: inputValue } } : undefined;
          const { data: userData } = await (await ApiService.GetUsers({ limit: 100, ...filters })).json();
          const { data: sitemateUserData } = await (
            await ApiService.GetUsers({ limit: 100, filters: { ...filters?.filters, userType: 'sitemate-users' } })
          ).json();

          const resultsMap = new Map();
          userData.forEach((user) => {
            if (user.firstName || user.lastName) {
              resultsMap.set(user.id, { label: EntityRefUtil.createUserFullNameRef(user), value: user.id });
            }
          });
          sitemateUserData.forEach((user) => {
            if (user.firstName || user.lastName) {
              resultsMap.set(user.id, { label: EntityRefUtil.createUserFullNameRef(user), value: user.id });
            }
          });

          if (value?.length) {
            const missingIds = Array.isArray(value) ? value.filter((id) => !resultsMap.has(id)) : [];
            if (missingIds.length) {
              const { data: missingUsers } = await (
                await ApiService.GetUsers({
                  limit: 100,
                  filters: { id: missingIds },
                })
              ).json();

              const { data: missingSitemateUsers } = await (
                await ApiService.GetUsers({
                  limit: 100,
                  filters: { id: missingIds, userType: 'sitemate-users' },
                })
              ).json();

              for (const user of missingUsers) {
                resultsMap.set(user.id, { label: EntityRefUtil.createUserFullNameRef(user), value: user.id });
              }

              for (const user of missingSitemateUsers) {
                resultsMap.set(user.id, { label: EntityRefUtil.createUserFullNameRef(user), value: user.id });
              }
            }
          }

          return Array.from(resultsMap.values());
        },
      },
    }),
    createTagsColumn(columnHelper, { header: 'Issue Tags', entity: TagEntity.Issue, variant: 'orange', canFilterUntagged: true }),
    columnHelper.accessor((row) => (row.repair?.repairDate ? formatDateWithUserTimezone(new Date(row.repair.repairDate), userTimezone) : null), {
      id: 'repairDate',
      header: 'Completed Date',
      cell: ({ getValue }) => {
        const value = getValue();
        return value ? <span>{value}</span> : <span className="text-secondary-text">-</span>;
      },
      enableSorting: true,
      enableGrouping: false,
      meta: {
        columnGroup: 'Repair',
        filterType: 'date-range',
      },
    }),
    ...(opts?.units
      ? opts.units.map((unit) =>
          columnHelper.accessor(
            (row) => {
              const elements = row.repair?.formRevision?.elements;
              if (!elements || !row.repair?.data) return null;
              const unitElement = elements.find((el) => el.kind === FormElementKind.UnitReading && (el.config as { unitId?: string })?.unitId === unit.id);
              if (!unitElement) return null;
              const answer = row.repair.data.find((a) => a.elementId === unitElement.id);
              const numericValue = Number(answer?.value);
              return answer?.value != null && Number.isFinite(numericValue) ? numericValue : null;
            },
            {
              id: `completedUnit-${unit.id}`,
              header: `Completed ${unit.name}`,
              cell: ({ getValue }) => {
                const value = getValue();
                return value != null ? (
                  <span>
                    {value.toLocaleString()} {unit.abbreviation}
                  </span>
                ) : (
                  <span className="text-secondary-text">-</span>
                );
              },
              enableSorting: false,
              enableGrouping: false,
              enableColumnFilter: false,
              meta: { columnGroup: 'Repair' },
            },
          ),
        )
      : []),
    columnHelper.accessor('daysOpen', {
      header: 'Days Open',
      enableGrouping: false,
      enableColumnFilter: false,
    }),
    columnHelper.accessor('category', {
      header: 'Category',
      getGroupingValue: (row) => row.category?.name || 'None',
      enableGrouping: true,
      enableSorting: true,
      cell: ({ getValue }) => {
        const value = getValue();
        return value ? (
          <div className="flex items-center gap-1">
            <Badge variant="violet" size="sm">
              {value.name}
            </Badge>
          </div>
        ) : (
          <span className="text-secondary-text">-</span>
        );
      },
      enableColumnFilter: true,
      meta: {
        filterType: 'async-multi-select',
        getFilterOptionsFn: async () => {
          const response = await (await ApiService.GetCategories({ limit: 500, filters: { fields: { entity: CategoryEntity.Issue } } })).json();
          return response.data.map((category) => ({
            label: category.name,
            value: category.id,
          }));
        },
      },
    }),
  ] as AccessorKeyColumnDef<IssueDTO>[];
};

export const groupOptions = [
  { label: 'None', accessor: '' },
  ...createAssetIssueTableColumns()
    .filter((column) => {
      const availableGroupColumns = ['severity', 'workOrder', 'repair', 'location', 'status', 'createdBy', 'category'];
      return !column.accessorKey && column.id ? availableGroupColumns.includes(column.id) : availableGroupColumns.includes(column.accessorKey);
    })
    .map((column) => ({ label: column.header?.toString() || '', accessor: !column.accessorKey && column.id ? column.id : column.accessorKey })),
] satisfies { label: string; accessor: string }[];

export const FILTER_FIELD_MAP: Record<
  NonNullable<ReturnType<typeof createAssetIssueTableColumns>[number]['id'] | ReturnType<typeof createAssetIssueTableColumns>[number]['accessorKey']>,
  keyof NonNullable<NonNullable<GetIssuesRequestDTO['filters']>['fields']>
> = {
  severity: 'severity',
  number: 'number',
  workOrder: 'workOrderNumber',
  repair: 'repairNumber',
  location: 'locationId',
  status: 'status',
  createdDate: 'createdDate',
  repairDate: 'repairDate',
  createdBy: 'createdById',
  tags: 'tagIds',
  category: 'categoryId',
};
